import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Trash2,
  CheckCircle2,
  Archive,
  Inbox,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const AdminLeadsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: leadData }, { data: statsData }] = await Promise.all([
        api.get("/admin/leads", {
          params: { status_filter: statusFilter, type_filter: typeFilter },
        }),
        api.get("/admin/leads/stats"),
      ]);
      setLeads(leadData);
      setStats(statsData);
      if (leadData.length && !selected) setSelected(leadData[0]);
      if (leadData.length && selected) {
        const found = leadData.find((l) => l.id === selected.id);
        setSelected(found || leadData[0] || null);
      }
      if (leadData.length === 0) setSelected(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update.");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted.");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete.");
    }
  };

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-brand-bg">
      {/* Top bar */}
      <header className="border-b border-brand-border bg-brand-surface">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-home-link"
            className="flex items-center gap-3"
          >
            <div className="w-8 h-8 bg-brand-ink flex items-center justify-center">
              <span className="font-heading text-brand-bg font-black text-base leading-none">
                J
              </span>
            </div>
            <div className="leading-none">
              <div className="font-heading font-black text-base tracking-tight text-brand-ink">
                JMJC · Admin
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-brand-inkSoft mt-1">
                Lead Dashboard
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-brand-inkSoft">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh"
              title="Refresh"
              className="p-2 border border-brand-border hover:border-brand-ink transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                strokeWidth={1.75}
              />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-brand-border hover:bg-brand-ink hover:text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="border-b border-brand-border">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-6 gap-px bg-brand-border">
          <StatCard label="Total" value={stats?.total ?? "—"} testid="stat-total" />
          <StatCard
            label="New"
            value={stats?.new ?? "—"}
            highlight
            testid="stat-new"
          />
          <StatCard
            label="Contacted"
            value={stats?.contacted ?? "—"}
            testid="stat-contacted"
          />
          <StatCard
            label="Archived"
            value={stats?.archived ?? "—"}
            testid="stat-archived"
          />
          <StatCard
            label="Contact form"
            value={stats?.contact ?? "—"}
            testid="stat-contact"
          />
          <StatCard
            label="Quote requests"
            value={stats?.quote ?? "—"}
            testid="stat-quote"
          />
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-brand-border py-4 bg-brand-bg">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 flex flex-wrap gap-3 items-center">
          <FilterGroup
            label="Status"
            options={["all", "new", "contacted", "archived"]}
            value={statusFilter}
            onChange={setStatusFilter}
            testidPrefix="status"
          />
          <span className="hidden md:inline w-px h-6 bg-brand-border" />
          <FilterGroup
            label="Type"
            options={["all", "contact", "quote"]}
            value={typeFilter}
            onChange={setTypeFilter}
            testidPrefix="type"
          />
        </div>
      </section>

      {/* Main grid */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        {/* List */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="bg-brand-surface border border-brand-border">
            {leads.length === 0 && !loading ? (
              <div className="p-10 text-center flex flex-col items-center gap-4">
                <Inbox className="w-8 h-8 text-brand-inkSoft" strokeWidth={1.5} />
                <p className="text-sm text-brand-inkSoft">
                  No leads match these filters yet.
                </p>
              </div>
            ) : (
              <ul data-testid="admin-lead-list" className="divide-y divide-brand-border max-h-[70vh] overflow-auto">
                {leads.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(l)}
                      data-testid={`admin-lead-item-${l.id}`}
                      className={`w-full text-left p-5 hover:bg-brand-subtle transition-colors ${
                        selected?.id === l.id ? "bg-brand-subtle" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <span
                          className={`text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-1 ${
                            l.type === "quote"
                              ? "bg-brand-cta text-white"
                              : "bg-brand-ink text-white"
                          }`}
                        >
                          {l.type}
                        </span>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-[0.18em] ${
                            l.status === "new"
                              ? "text-brand-cta"
                              : l.status === "contacted"
                              ? "text-emerald-700"
                              : "text-brand-inkSoft"
                          }`}
                        >
                          {l.status}
                        </span>
                      </div>
                      <p className="font-heading font-bold text-base text-brand-ink truncate">
                        {leadDisplayName(l)}
                      </p>
                      <p className="text-xs text-brand-inkSoft truncate mt-1">
                        {l.payload.email}
                      </p>
                      <p className="text-[10px] text-brand-inkSoft/70 mt-2">
                        {formatDate(l.created_at)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 xl:col-span-8">
          {selected ? (
            <div
              key={selected.id}
              data-testid="admin-lead-detail"
              className="bg-brand-surface border border-brand-border p-8 lg:p-10"
            >
              <div className="flex items-start justify-between gap-4 mb-8">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 ${
                        selected.type === "quote"
                          ? "bg-brand-cta text-white"
                          : "bg-brand-ink text-white"
                      }`}
                    >
                      {selected.type} lead
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-inkSoft">
                      {selected.status}
                    </span>
                  </div>
                  <h2 className="font-heading text-3xl font-black tracking-tighter text-brand-ink">
                    {leadDisplayName(selected)}
                  </h2>
                  <p className="text-xs text-brand-inkSoft mt-2">
                    Received {formatDate(selected.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => deleteLead(selected.id)}
                  data-testid="admin-lead-delete"
                  title="Delete"
                  className="p-2 border border-brand-border hover:border-brand-cta hover:text-brand-cta transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>

              {/* Contact strip */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <ContactChip
                  icon={Mail}
                  label={selected.payload.email}
                  href={`mailto:${selected.payload.email}`}
                  testid="detail-email"
                />
                {selected.payload.phone && (
                  <ContactChip
                    icon={Phone}
                    label={selected.payload.phone}
                    href={`tel:${selected.payload.phone.replace(/[^0-9+]/g, "")}`}
                    testid="detail-phone"
                  />
                )}
                {selected.payload.zip && (
                  <ContactChip
                    icon={MapPin}
                    label={`ZIP ${selected.payload.zip}`}
                    testid="detail-zip"
                  />
                )}
              </div>

              {/* Details */}
              <div className="space-y-6 pb-8 border-b border-brand-border">
                {selected.type === "quote" ? (
                  <>
                    <DetailRow label="Service" value={selected.payload.service} />
                    <DetailRow label="Budget" value={selected.payload.budget || "—"} />
                    <DetailRow label="Timeline" value={selected.payload.timeline || "—"} />
                    <DetailRow
                      label="Project Details"
                      value={selected.payload.details || "—"}
                      multiline
                    />
                  </>
                ) : (
                  <DetailRow
                    label="Message"
                    value={selected.payload.message}
                    multiline
                  />
                )}
              </div>

              {/* Actions */}
              <div className="pt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => updateStatus(selected.id, "contacted")}
                  disabled={selected.status === "contacted"}
                  data-testid="admin-mark-contacted"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-brand-ink hover:bg-brand-cta text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                  Mark Contacted
                </button>
                <button
                  type="button"
                  onClick={() => updateStatus(selected.id, "archived")}
                  disabled={selected.status === "archived"}
                  data-testid="admin-mark-archived"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-brand-border hover:border-brand-ink text-brand-ink text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Archive className="w-4 h-4" strokeWidth={2} />
                  Archive
                </button>
                <button
                  type="button"
                  onClick={() => updateStatus(selected.id, "new")}
                  disabled={selected.status === "new"}
                  data-testid="admin-mark-new"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-brand-border hover:border-brand-ink text-brand-ink text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Reopen
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-brand-surface border border-brand-border p-12 text-center">
              <Inbox className="w-8 h-8 text-brand-inkSoft mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-brand-inkSoft">
                Select a lead on the left to see the details.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// --- Sub-components ---
const StatCard = ({ label, value, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`bg-brand-surface p-6 flex flex-col gap-2 ${
      highlight ? "border-l-2 border-brand-cta" : ""
    }`}
  >
    <span className="font-heading text-3xl lg:text-4xl font-black tracking-tighter text-brand-ink">
      {value}
    </span>
    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
      {label}
    </span>
  </div>
);

const FilterGroup = ({ label, options, value, onChange, testidPrefix }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
      {label}
    </span>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`filter-${testidPrefix}-${opt}`}
        onClick={() => onChange(opt)}
        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] border transition-colors ${
          value === opt
            ? "bg-brand-ink text-white border-brand-ink"
            : "bg-transparent text-brand-ink border-brand-border hover:border-brand-ink"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const ContactChip = ({ icon: Icon, label, href, testid }) => {
  const inner = (
    <div className="flex items-center gap-3 p-4 bg-brand-subtle border border-brand-border">
      <Icon className="w-4 h-4 text-brand-cta shrink-0" strokeWidth={1.75} />
      <span className="text-sm text-brand-ink truncate">{label}</span>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block hover:opacity-80 transition-opacity">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
};

const DetailRow = ({ label, value, multiline }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-6">
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft pt-1">
      {label}
    </p>
    <p
      className={`md:col-span-3 text-base text-brand-ink ${
        multiline ? "whitespace-pre-wrap leading-relaxed" : ""
      }`}
    >
      {value}
    </p>
  </div>
);

function leadDisplayName(l) {
  if (l.type === "quote") {
    return `${l.payload.firstName || ""} ${l.payload.lastName || ""}`.trim() || "—";
  }
  return l.payload.name || "—";
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default AdminLeadsPage;
